import React, { useState } from "react";
import { Plus, Truck, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { useList, useDeleteOne, useBulkDelete } from "@/hooks/useApi";
import { useAuth } from "@/context/AuthContext";
import { useQueryClient } from "@tanstack/react-query";
import { PageHeader, Loading, EmptyState, fmt } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";

export default function Dispatch() {
  const { canWrite, user } = useAuth();
  const qc = useQueryClient();
  const delOne = useDeleteOne((id) => `/dispatch/${id}`, ["dispatch"]);
  const clearAll = useBulkDelete((r) => `/dispatch/${r.id}`, ["dispatch"]);
  const { data: rows, isLoading } = useList("dispatch", "/dispatch");
  const { data: orders = [] } = useList("orders", "/orders");
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ order_ref: "", customer: "", quantity: "", cartons: "", transporter: "", vehicle_number: "", tracking_number: "", dispatch_date: "", destination: "" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    if (!f.order_ref) return toast.error("Select order");
    const o = orders.find((x) => x.id === f.order_ref);
    try { await api.post("/dispatch", { ...f, order_id: o?.order_id, customer: f.customer || o?.buyer, quantity: Number(f.quantity) || 0, cartons: Number(f.cartons) || 0 }); toast.success("Dispatch recorded"); qc.invalidateQueries(); setOpen(false); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  if (isLoading) return <Loading />;
  return (
    <div>
      <PageHeader title="Dispatch" subtitle="Shipment & delivery tracking" testid="dispatch-header">
        {canWrite("dispatch") && <Button onClick={() => setOpen(true)} className="gap-2" data-testid="new-dispatch-btn"><Plus className="w-4 h-4" /> New Dispatch</Button>}
      </PageHeader>
      <Card className="overflow-hidden">
        {!rows?.length ? <EmptyState title="No dispatches" /> : (
          <div className="overflow-x-auto"><Table>
            <TableHeader><TableRow><TableHead>Dispatch</TableHead><TableHead>Order</TableHead><TableHead>Customer</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Cartons</TableHead><TableHead>Transporter</TableHead><TableHead>Vehicle</TableHead><TableHead>Tracking</TableHead><TableHead>Date</TableHead><TableHead>Destination</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>{rows.map((r) => (
              <TableRow key={r.id} data-testid={`dispatch-row-${r.dispatch_id}`}>
                <TableCell className="font-num text-primary font-semibold">{r.dispatch_id}</TableCell><TableCell className="font-num">{r.order_id}</TableCell>
                <TableCell className="text-sm max-w-[130px] truncate">{r.customer}</TableCell><TableCell className="text-right font-num">{fmt(r.quantity)}</TableCell>
                <TableCell className="text-right font-num">{r.cartons}</TableCell><TableCell className="text-sm">{r.transporter}</TableCell>
                <TableCell className="text-sm font-num">{r.vehicle_number}</TableCell><TableCell className="text-sm font-num">{r.tracking_number}</TableCell>
                <TableCell className="text-sm">{r.dispatch_date}</TableCell><TableCell className="text-sm">{r.destination}</TableCell>
                <TableCell>{canWrite("dispatch") && <Button size="icon" variant="ghost" onClick={() => delOne(r.id, "Delete this dispatch record?")} data-testid={`delete-dispatch-${r.dispatch_id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>}</TableCell>
              </TableRow>
            ))}</TableBody>
          </Table></div>
        )}
      </Card>
      <Dialog open={open} onOpenChange={setOpen}><DialogContent data-testid="dispatch-form">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Truck className="w-5 h-5" /> New Dispatch</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Order</Label>
            <Select value={f.order_ref} onValueChange={(v) => set("order_ref", v)}>
              <SelectTrigger data-testid="dispatch-order-select"><SelectValue placeholder="Order" /></SelectTrigger>
              <SelectContent>{orders.map((o) => <SelectItem key={o.id} value={o.id}>{o.order_id} — {o.buyer}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Quantity</Label><Input type="number" value={f.quantity} onChange={(e) => set("quantity", e.target.value)} /></div>
          <div><Label># Cartons</Label><Input type="number" value={f.cartons} onChange={(e) => set("cartons", e.target.value)} /></div>
          <div><Label>Transporter</Label><Input value={f.transporter} onChange={(e) => set("transporter", e.target.value)} /></div>
          <div><Label>Vehicle Number</Label><Input value={f.vehicle_number} onChange={(e) => set("vehicle_number", e.target.value)} /></div>
          <div><Label>Tracking / LR</Label><Input value={f.tracking_number} onChange={(e) => set("tracking_number", e.target.value)} /></div>
          <div><Label>Dispatch Date</Label><Input type="date" value={f.dispatch_date} onChange={(e) => set("dispatch_date", e.target.value)} /></div>
          <div className="col-span-2"><Label>Destination</Label><Input value={f.destination} onChange={(e) => set("destination", e.target.value)} /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={submit} data-testid="dispatch-save-btn">Save</Button></DialogFooter>
      </DialogContent></Dialog>
    </div>
  );
}
